<template>
  <div class="max-w-6xl mx-auto px-4 py-6">
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-2xl font-bold text-gray-800">OpsFlow</h1>
    </div>

    <!-- Tab bar -->
    <div class="border-b border-gray-200 mb-6">
      <nav class="flex space-x-6">
        <button
          @click="activeTab = 'pending'"
          :class="tabClass('pending')"
          class="pb-3 px-1 text-sm font-medium border-b-2 transition-colors"
        >
          Pending
          <span
            v-if="pendingCount > 0"
            class="ml-1.5 inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold leading-none text-white bg-blue-600 rounded-full"
          >{{ pendingCount }}</span>
        </button>
        <button
          @click="activeTab = 'completed'"
          :class="tabClass('completed')"
          class="pb-3 px-1 text-sm font-medium border-b-2 transition-colors"
        >Completed</button>
        <button
          @click="activeTab = 'settings'"
          :class="tabClass('settings')"
          class="pb-3 px-1 text-sm font-medium border-b-2 transition-colors"
        >Settings</button>
        <button
          @click="activeTab = 'templates'"
          :class="tabClass('templates')"
          class="pb-3 px-1 text-sm font-medium border-b-2 transition-colors"
        >Templates</button>
      </nav>
    </div>

    <!-- Tab content -->
    <PendingTable
      v-if="activeTab === 'pending'"
      :forms="pendingForms"
      :templates="templates"
      @open="openForm"
    />
    <CompletedTable
      v-if="activeTab === 'completed'"
      :forms="completedForms"
      :templates="templates"
    />
    <SettingsPanel
      v-if="activeTab === 'settings'"
      :locationId="locationId"
      :companyId="companyId"
      :templates="templates"
    />
    <TemplateBuilder
      v-if="activeTab === 'templates'"
      :locationId="locationId"
      :templates="templates"
      @templates-changed="fetchTemplates"
    />

    <!-- Form fill modal -->
    <FormFillModal
      v-if="selectedForm"
      :form="selectedForm"
      :template="selectedTemplate"
      :locationId="locationId"
      :companyId="companyId"
      :userName="userName"
      @close="selectedForm = null"
      @submitted="onFormSubmitted"
    />

    <!-- Writeback results toast -->
    <div
      v-if="writebackBanner"
      class="fixed bottom-6 right-6 z-50 w-80 rounded-lg shadow-lg border p-4"
      :class="writebackBanner.allOk ? 'bg-green-50 border-green-300' : 'bg-yellow-50 border-yellow-300'"
    >
      <div class="flex items-start justify-between mb-2">
        <span class="font-semibold text-sm" :class="writebackBanner.allOk ? 'text-green-800' : 'text-yellow-800'">
          Writeback Results
        </span>
        <button @click="writebackBanner = null" class="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
      </div>
      <ul class="space-y-1 text-sm">
        <li v-for="item in writebackBanner.items" :key="item.label" class="flex items-center gap-2">
          <span v-if="item.ok" class="text-green-600">&#10003;</span>
          <span v-else class="text-red-500">&#10007;</span>
          <span :class="item.ok ? 'text-green-800' : 'text-yellow-800'">{{ item.label }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import PendingTable from './PendingTable.vue'
import CompletedTable from './CompletedTable.vue'
import SettingsPanel from './SettingsPanel.vue'
import FormFillModal from './FormFillModal.vue'
import TemplateBuilder from './TemplateBuilder.vue'

export default {
  name: 'OpsFlowApp',
  components: { PendingTable, CompletedTable, SettingsPanel, FormFillModal, TemplateBuilder },
  props: {
    locationId: { type: String, required: true },
    companyId: { type: String, default: null },
    userName: { type: String, default: null }
  },
  data() {
    return {
      activeTab: 'pending',
      forms: [],
      templates: [],
      selectedForm: null,
      writebackBanner: null
    }
  },
  computed: {
    pendingForms() {
      return this.forms.filter(f => f.status === 'pending')
    },
    completedForms() {
      return this.forms.filter(f => f.status === 'completed')
    },
    pendingCount() {
      return this.pendingForms.length
    },
    selectedTemplate() {
      if (!this.selectedForm) return null
      return this.templates.find(t => t.id === this.selectedForm.templateId) || null
    }
  },
  async mounted() {
    await Promise.all([this.fetchTemplates(), this.fetchForms()])
  },
  methods: {
    async fetchTemplates() {
      try {
        const res = await fetch(`/opsflow/templates?locationId=${encodeURIComponent(this.locationId)}`)
        this.templates = await res.json()
      } catch (e) {
        console.error('Failed to fetch templates:', e)
      }
    },
    async fetchForms() {
      try {
        const params = new URLSearchParams({ locationId: this.locationId })
        if (this.companyId) params.set('companyId', this.companyId)
        const res = await fetch(`/opsflow/forms?${params}`)
        const data = await res.json()
        this.forms = data.forms || []
      } catch (e) {
        console.error('Failed to fetch forms:', e)
      }
    },
    openForm(form) {
      this.selectedForm = form
    },
    async onFormSubmitted(result) {
      this.selectedForm = null
      await this.fetchForms()

      const wb = result?.writebackResults
      if (wb && Object.keys(wb).length > 0) {
        const labels = { note: 'Note', customFields: 'Custom Fields', task: 'Task', pipelineMove: 'Pipeline Move' }
        const items = Object.entries(wb).map(([key, ok]) => ({ label: labels[key] || key, ok }))
        this.writebackBanner = { items, allOk: items.every(i => i.ok) }
        setTimeout(() => { this.writebackBanner = null }, 6000)
      }
    },
    tabClass(tab) {
      return this.activeTab === tab
        ? 'border-blue-600 text-blue-600'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
    }
  }
}
</script>
